import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { CAPABILITIES, resolveCapability, type EntitlementContext } from '@/services/entitlements';
import { visibleDestinations } from '@/app/navigation';

/**
 * Primary bottom navigation: Home, Accounts, Payments, Cards, More.
 *
 * Binding rules:
 *  - XS-SMEM-TT-R006-A2: an unentitled destination is HIDDEN, not greyed out.
 *  - TT-R145-01: no duplicated destinations for the same feature. "More" is a
 *    hub for sections that are not primary tabs; nothing here also appears
 *    inside More.
 *  - Approvals may take the Payments slot for an approver-first role, per
 *    baseline_handoff IA — decided from effective permissions, not hard-coded.
 */

type NavIcon = (props: { active: boolean }) => ReactNode;

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const IconHome: NavIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.8V20h14V9.8" />
  </svg>
);

const IconAccounts: NavIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <rect x="3" y="6" width="18" height="13" rx="2.5" />
    <path d="M3 10.5h18" />
  </svg>
);

const IconPayments: NavIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M12 19V5" />
    <path d="m6 11 6-6 6 6" />
  </svg>
);

const IconApprovals: NavIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const IconCards: NavIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
    <path d="M2.5 9.5h19" />
    <path d="M6 14.5h4" />
  </svg>
);

const IconMore: NavIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="5" cy="12" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="19" cy="12" r="1.4" />
  </svg>
);

export type NavItemSpec = {
  readonly id: 'home' | 'accounts' | 'payments' | 'approvals' | 'cards' | 'more';
  readonly label: string;
  readonly route: string;
  readonly icon: NavIcon;
  readonly badgeCount?: number;
};

const ICONS: Readonly<Record<string, NavIcon>> = {
  home: IconHome,
  accounts: IconAccounts,
  payments: IconPayments,
  approvals: IconApprovals,
  cards: IconCards,
};

/**
 * Build the tab bar from the shared registry, so a destination declared as a
 * tab can never also surface under More (TT-R145-01).
 */
export function buildNavItems(
  ctx: EntitlementContext,
  counts: { readonly approvals: number },
): readonly NavItemSpec[] {
  const entitled = visibleDestinations(ctx, 'tab');

  const canPay = resolveCapability(ctx, CAPABILITIES.paymentsCreate).allowed;

  const items: NavItemSpec[] = [];
  for (const destination of entitled) {
    // Payments and Approvals share one slot: an approver who cannot create
    // payments gets Approvals there rather than a tab they can never use.
    if (destination.id === 'approvals' && canPay) continue;
    if (destination.id === 'payments' && !canPay) continue;

    const icon = ICONS[destination.id];
    if (!icon) continue;
    items.push({
      id: destination.id as NavItemSpec['id'],
      label: destination.label,
      route: destination.route,
      icon,
      ...(destination.id === 'approvals' && counts.approvals > 0
        ? { badgeCount: counts.approvals }
        : {}),
    });
  }

  items.push({ id: 'more', label: 'More', route: '/mobile/more', icon: IconMore });
  return items;
}

export function BottomNav({ items }: { items: readonly NavItemSpec[] }) {
  return (
    <nav className="tm-nav" aria-label="Primary">
      {items.map((item) => (
        <NavLink
          key={item.id}
          to={item.route}
          className="tm-nav__item"
          // Home must not stay highlighted on nested routes.
          end={item.route === '/mobile/home'}
        >
          {({ isActive }) => (
            <>
              <item.icon active={isActive} />
              <span>{item.label}</span>
              {item.badgeCount ? (
                <span className="tm-count tm-nav__badge">
                  {item.badgeCount}
                  <span className="tm-visually-hidden"> items need attention</span>
                </span>
              ) : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
